import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register service worker for PWA / offline shell.
// The build id versions the SW URL + cache name: each deployment unregisters the
// previous worker and purges its cache so returning visitors get the fresh app.
// If a newer worker takes control after the page already painted (classic PWA
// "stale shell flashes, then fresh content swaps in" symptom — especially right
// after a deploy), reload once immediately so the user only ever sees the
// current version instead of the old one flashing first.
if ("serviceWorker" in navigator) {
  const BUILD_ID = process.env.REACT_APP_BUILD_ID || "dev";
  const swUrl = `/service-worker.js?v=${BUILD_ID}`;

  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) return;
    refreshing = true;
    window.location.reload();
  });

  window.addEventListener("load", async () => {
    try {
      // Unregister any stale service worker from a previous deployment
      const regs = await navigator.serviceWorker.getRegistrations();
      let hadStaleWorker = false;
      for (const reg of regs) {
        const scriptUrl =
          (reg.active && reg.active.scriptURL) ||
          (reg.waiting && reg.waiting.scriptURL) ||
          (reg.installing && reg.installing.scriptURL) || "";
        if (scriptUrl && !scriptUrl.includes(`v=${BUILD_ID}`)) {
          hadStaleWorker = true;
          await reg.unregister();
        }
      }
      // Purge caches left behind by previous versions
      if (window.caches) {
        const keys = await caches.keys();
        await Promise.all(
          keys.filter((k) => k !== `swiftlink-${BUILD_ID}`).map((k) => caches.delete(k))
        );
      }
      await navigator.serviceWorker.register(swUrl);
      // We were running on an outdated worker when this page loaded (i.e. it
      // served a stale cached shell first) — reload once now that the fresh
      // worker + cache are in place, so the current build shows immediately.
      if (hadStaleWorker) window.location.reload();
    } catch {
      // best effort — fail silently
    }
  });
}
